import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, GraduationCap, Hospital, Utensils, Trees, Layers } from 'lucide-react';
import { NearbyAmenities } from '@/components/property-detail/NearbyAmenities';

interface ProjectLocationProps {
  city: string;
  subCity?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  showNearby?: boolean;
}

type CatKey = 'all' | 'school' | 'hospital' | 'restaurant' | 'park';

const CATEGORIES: { key: CatKey; label: string; icon: any; query?: string; color: string }[] = [
  { key: 'all',        label: 'All',         icon: Layers,        color: '#16a34a' },
  { key: 'school',     label: 'Schools',     icon: GraduationCap, query: 'node["amenity"~"school|university|college"]', color: '#2563eb' },
  { key: 'restaurant', label: 'Restaurants', icon: Utensils,      query: 'node["amenity"~"restaurant|cafe|fast_food"]', color: '#ea580c' },
  { key: 'hospital',   label: 'Hospitals',   icon: Hospital,      query: 'node["amenity"~"hospital|clinic|pharmacy"]', color: '#dc2626' },
  { key: 'park',       label: 'Parks',       icon: Trees,         query: 'node["leisure"~"park|garden"]', color: '#15803d' },
];

const pinIcon = (color: string, isProject = false) =>
  L.divIcon({
    className: '',
    html: `<div style="
      width:${isProject ? 28 : 22}px;height:${isProject ? 28 : 22}px;
      background:${color};border:2px solid #fff;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);
      display:flex;align-items:center;justify-content:center;
    "><div style="width:8px;height:8px;background:#fff;border-radius:50%;transform:rotate(45deg);"></div></div>`,
    iconSize: [isProject ? 28 : 22, isProject ? 28 : 22],
    iconAnchor: [isProject ? 14 : 11, isProject ? 28 : 22],
  });

function distMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

function Recenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  map.setView([lat, lng], map.getZoom());
  return null;
}

export function ProjectLocation({ city, subCity, address, latitude, longitude, showNearby = true }: ProjectLocationProps) {
  const hasCoords = latitude != null && longitude != null;
  const [active, setActive] = useState<CatKey>('all');

  const targets = useMemo(
    () => active === 'all' ? CATEGORIES.filter(c => c.query) : CATEGORIES.filter(c => c.key === active),
    [active]
  );

  const { data: markers = [] } = useQuery({
    queryKey: ['project-map-amenities', latitude, longitude, active],
    enabled: hasCoords,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const radius = 1500;
      const results: any[] = [];
      await Promise.all(targets.map(async (cat) => {
        const q = `[out:json][timeout:15];(${cat.query}(around:${radius},${latitude},${longitude}););out body 25;`;
        try {
          const res = await fetch('https://overpass-api.de/api/interpreter', {
            method: 'POST',
            body: 'data=' + encodeURIComponent(q),
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          });
          if (!res.ok) return;
          const json = await res.json();
          (json.elements || []).forEach((el: any) => {
            if (!el.tags?.name) return;
            results.push({
              id: `${cat.key}-${el.id}`,
              name: el.tags.name,
              lat: el.lat,
              lon: el.lon,
              category: cat.key,
              color: cat.color,
              dist: distMeters(latitude as number, longitude as number, el.lat, el.lon),
            });
          });
        } catch { /* ignore */ }
      }));
      return results.sort((a, b) => a.dist - b.dist).slice(0, 60);
    },
  });

  return (
    <section className="max-w-full overflow-hidden space-y-6">
      <div>
        <h2 className="font-display text-lg md:text-xl font-bold mb-3 md:mb-4">Location</h2>

        {hasCoords ? (
          <div className="relative aspect-[16/9] w-full max-w-full rounded-xl overflow-hidden border">
            {/* Category chip overlay */}
            <div className="absolute top-3 left-3 z-[400] flex flex-wrap gap-1.5 max-w-[calc(100%-1.5rem)]">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const isActive = c.key === active;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActive(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 shadow-sm ${
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-white text-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {c.label}
                  </button>
                );
              })}
            </div>

            <MapContainer
              center={[latitude as number, longitude as number]}
              zoom={15}
              scrollWheelZoom={false}
              className="h-full w-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Recenter lat={latitude as number} lng={longitude as number} />
              <Marker position={[latitude as number, longitude as number]} icon={pinIcon('#dc2626', true)}>
                <Popup>
                  <div className="font-semibold">{city}{subCity ? `, ${subCity}` : ''}</div>
                  {address && <div className="text-xs text-muted-foreground">{address}</div>}
                </Popup>
              </Marker>
              {markers.map(m => (
                <Marker key={m.id} position={[m.lat, m.lon]} icon={pinIcon(m.color)}>
                  <Popup>
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.dist < 1000 ? `${m.dist} m` : `${(m.dist / 1000).toFixed(1)} km`} away
                    </div>
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${m.lat}&mlon=${m.lon}#map=18/${m.lat}/${m.lon}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-primary"
                    >
                      Open in OSM ↗
                    </a>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : (
          <div className="border rounded-xl p-4 md:p-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm break-words">{city}{subCity ? `, ${subCity}` : ''}</div>
                {address && <div className="text-sm text-muted-foreground break-words">{address}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      {showNearby && <NearbyAmenities latitude={latitude} longitude={longitude} />}
    </section>
  );
}
